import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type {
  CancelPolicyStaticContextValue,
  CancelPolicyStepContextValue,
} from "./types";

// ---------------------------------------------------------------------------
// Static context — stable booking + page config
// ---------------------------------------------------------------------------

const CancelPolicyStaticContext =
  createContext<CancelPolicyStaticContextValue | null>(null);

// ---------------------------------------------------------------------------
// Step context — volatile step-flow state
// ---------------------------------------------------------------------------

const CancelPolicyStepContext =
  createContext<CancelPolicyStepContextValue | null>(null);

// ---------------------------------------------------------------------------
// Providers
// ---------------------------------------------------------------------------

export const CancelPolicyProvider = ({
  staticValue,
  stepValue,
  children,
}: {
  staticValue: CancelPolicyStaticContextValue;
  stepValue: CancelPolicyStepContextValue;
  children: ReactNode;
}) => (
  <CancelPolicyStaticContext.Provider value={staticValue}>
    <CancelPolicyStepContext.Provider value={stepValue}>
      {children}
    </CancelPolicyStepContext.Provider>
  </CancelPolicyStaticContext.Provider>
);

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Access stable booking/page data only.
 * Components using this hook will NOT re-render on step interactions.
 */
export const useCancelPolicyStatic = (): CancelPolicyStaticContextValue => {
  const ctx = useContext(CancelPolicyStaticContext);
  if (!ctx) {
    throw new Error(
      "useCancelPolicyStatic must be used within CancelPolicyProvider"
    );
  }
  return ctx;
};

/**
 * Access volatile step-flow state.
 * Components using this hook re-render on every step interaction.
 */
export const useCancelPolicyStep = (): CancelPolicyStepContextValue => {
  const ctx = useContext(CancelPolicyStepContext);
  if (!ctx) {
    throw new Error(
      "useCancelPolicyStep must be used within CancelPolicyProvider"
    );
  }
  return ctx;
};

/**
 * Combined hook for components that need both static and step-flow data.
 * Subscribes to both contexts so re-renders on any change.
 */
export const useCancelPolicy = () => ({
  ...useCancelPolicyStatic(),
  ...useCancelPolicyStep(),
});
